// StudyApp — entry for /study/<condition>/<room>. For the mockup it resolves the
// condition straight from the URL so all four cells are reachable without the backend.
import React, { useEffect, useState } from "react";
import "./study.css";
import FactorialDemo from "./conditions/FactorialDemo";
import LiveStudy from "./conditions/LiveStudy";
import { CONDITION_CODES } from "./shared/conditionContract";
import {
  STUDY_MIN_VIEWPORT,
  isStudyViewportEligible,
  readStudyViewport,
} from "./shared/viewport";

function useStudyViewport() {
  const [viewport, setViewport] = useState(() => readStudyViewport());

  useEffect(() => {
    let frame = null;
    const update = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = null;
        setViewport(readStudyViewport());
      });
    };
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);

  return viewport;
}

function ViewportRequirement({ viewport, progressPreserved = false }) {
  return (
    <div className="study-viewport-gate" role="alert" aria-live="assertive">
      <div className="study-viewport-card">
        <div className="study-viewport-icon" aria-hidden="true">↔</div>
        <h1>Use a larger study window</h1>
        <p>
          This task requires a laptop or desktop-sized browser window so the scenario
          and report assistant remain visible at the same time.
        </p>
        <p>
          Maximize this window and reset browser zoom if needed. If you are using a
          phone, please reopen the study on a laptop or desktop.
        </p>
        {progressPreserved && (
          <p className="study-viewport-saved">Your task progress has been preserved.</p>
        )}
        <div className="study-viewport-measurements">
          <span>Available: {viewport.width} × {viewport.height}</span>
          <span>
            Required: {STUDY_MIN_VIEWPORT.width} × {STUDY_MIN_VIEWPORT.height}
          </span>
        </div>
      </div>
    </div>
  );
}

function StudyViewportGate({ children }) {
  const viewport = useStudyViewport();
  const eligible = isStudyViewportEligible(viewport);
  const [admitted, setAdmitted] = useState(() => eligible);

  useEffect(() => {
    if (eligible) setAdmitted(true);
  }, [eligible]);

  if (!admitted) return <ViewportRequirement viewport={viewport} />;

  return (
    <div className="study-viewport-shell">
      <div
        className={`study-viewport-content ${eligible ? "" : "is-blocked"}`}
        aria-hidden={eligible ? undefined : true}
      >
        {children}
      </div>
      {!eligible && <ViewportRequirement viewport={viewport} progressPreserved />}
    </div>
  );
}

function PreviewMenu() {
  const links = [
    { href: "/study/demo/INSTANT_OPT", code: "INSTANT_OPT", label: "Instant · optional", desc: "Cards appear after proposals; an unreviewed Card does not block the conversation." },
    { href: "/study/demo/INSTANT_REQ", code: "INSTANT_REQ", label: "Instant · required", desc: "Cards appear after proposals; a status is required before the conversation continues." },
    { href: "/study/demo/FINAL_OPT", code: "FINAL_OPT", label: "Final · optional", desc: "All Cards appear after entry; unreviewed Cards do not block Final Review." },
    { href: "/study/demo/FINAL_REQ", code: "FINAL_REQ", label: "Final · required", desc: "All Cards appear after entry; every Card needs a status before Final Review." },
  ];
  return (
    <div className="study-root">
      <div className="study-menu">
        <h1>Study UI preview</h1>
        <p className="pane-sub">Mockups with placeholder data. Open each condition:</p>
        {links.map((l) => (
          <a className="menu-card" href={l.href} key={l.code}>
            <span className="code">{l.code}</span> · {l.label}
            <div className="desc">{l.desc}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function StudyApp() {
  const parts = window.location.pathname.split("/").filter(Boolean); // ["study", cond, room]
  const demoMode = parts[1]?.toLowerCase() === "demo";
  const cond = (demoMode ? parts[2] : parts[1] || "").toLowerCase();
  const room = demoMode ? "demo" : parts[2];
  const session = new URLSearchParams(window.location.search).get("session") || "";
  const isMock = demoMode || room === "mock";
  const [cfg, setCfg] = useState(isMock ? {} : null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!room || isMock) return;
    if (!session) {
      setError("missing-session");
      return;
    }

    let cancelled = false;
    fetch(`/api/room/${encodeURIComponent(room)}/condition?session=${encodeURIComponent(session)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`condition fetch failed: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!cancelled) setCfg(data);
      })
      .catch(() => {
        if (!cancelled) setError("invalid-session");
      });
    return () => {
      cancelled = true;
    };
  }, [room, isMock, session]);

  if (!cond) return <PreviewMenu />;
  if (error) {
    return (
      <div className="study-root">
        <div className="study-menu">
          <h1>Study link invalid or expired</h1>
          <p className="pane-sub">Please return to the survey and reopen the study frame.</p>
        </div>
      </div>
    );
  }
  if (!isMock && cfg === null) {
    return (
      <div className="study-root">
        <div className="study-menu">
          <h1>Loading study</h1>
          <p className="pane-sub">Preparing your study session.</p>
        </div>
      </div>
    );
  }

  const resolvedCond = (cfg?.condition || cond).toLowerCase();
  if (isMock) {
    const demoCondition = resolvedCond.toUpperCase();
    if (!CONDITION_CODES.includes(demoCondition)) return <PreviewMenu />;
    return (
      <StudyViewportGate>
        <FactorialDemo condition={demoCondition} />
      </StudyViewportGate>
    );
  }

  return (
    <StudyViewportGate>
      <LiveStudy
        condition={(cfg?.condition || cond || "INSTANT_OPT").toUpperCase()}
        room={room}
        cfg={cfg}
        studySession={session}
      />
    </StudyViewportGate>
  );
}
